import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import moment from 'moment';

// Material UI
import {
	Button,
	IconButton,
	Snackbar,
	SnackbarContent,
	withStyles,
	Paper,
} from '@material-ui/core';
import Slide from '@material-ui/core/Slide';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CloseIcon from '@material-ui/icons/Close';
import { green } from '@material-ui/core/colors';

import { post } from '../../../modules/utility';
import AuthUserContainer from '../../../containers/AuthUser';
import PtoRequestContainer from '../../../containers/PtoRequest';
import EnhancedTable from '../../../components/table/EnhancedTable';
import FilterHeader from '../../../components/table/FilterHeader';
import { getGlobalClasses } from '../../../themes';
import { formatPtoRequestHours, ptoTypeToDisplay, renderStatus } from '../util';

const SlideUpTransition = React.forwardRef(function Transition(props, ref) {
	return <Slide direction="up" ref={ref} {...props} />;
});
const styles = (theme) => {
	return {
		...getGlobalClasses(theme),
		snackbarSuccess: {
			backgroundColor: green[600],
		},
		snackbarIcon: {
			opacity: 0.9,
			marginRight: theme.spacing(1),
			fontSize: 20,
		},
		snackbarMessage: {
			display: 'flex',
			alignItems: 'center',
		},
	};
};

class PTOExportedDashboard extends Component {
	constructor(props) {
		super(props);
		this.state = {
			order: 'asc',
			orderBy: 'displayName',
			isReopening: false,
		};
	}

	componentDidMount() {
		const { myPtoApprovals, searchPtoRequests } = this.props;
		if (!myPtoApprovals) {
			searchPtoRequests({
				params: {
					searchFor: 'myApprovals',
				},
			});
		} else {
			this.formatPtoRequests();
		}
	}

	componentDidUpdate(prevProps, prevState) {
		const { myPtoApprovals } = this.props;
		if (prevProps.myPtoApprovals !== myPtoApprovals) {
			this.formatPtoRequests();
		}
	}

	handleSnackbarClose = (_event, reason) => {
		if (reason === 'clickaway') {
			return;
		}
		this.setState({ snackbarIsOpen: false });
	};

	updateExported({ row, action }) {
		const { ptoRequestId } = row;
		const { showErrors, searchPtoRequests } = this.props;
		this.setState({ isReopening: true });

		post({
			method: 'ptoRequest.update',
			params: {
				ptoRequestId,
				action,
			},
			callback: (err, _r) => {
				if (err) {
					this.setState({ isReopening: false });
					return showErrors(err);
				}

				searchPtoRequests({
					params: {
						searchFor: 'myApprovals',
					},
					callback: (err, _r) => {
						this.setState({ isReopening: false });
						if (err) {
							return showErrors(err);
						}

						return this.setState({
							snackbarIsOpen: true,
						});
					},
				});
			},
		});
	}

	formatPtoRequests = () => {
		const { classes } = this.props;
		const { myPtoApprovals, userMap } = this.props;
		const hrExportedRequests = [];

		const newState = {};
		myPtoApprovals.forEach((ptoRequest) => {
			const {
				assignedToHrUserId,
				assignedToSupervisorUserId,
				// createdByUserId,
				// dateAndHours,
				// ptoCashOutTotalHours,
				paidType,
				ptoRequestId,
				// ptoRequestLogs, //[]
				requestedForUserId,
				dateCreated,
				status,
				linkedToRequiredRequestId,
				isExported,
			} = ptoRequest;

			const { totalHours, totalDays, rangeDisplay, rangeSort } = formatPtoRequestHours(ptoRequest);

			const requestor = userMap[requestedForUserId] || { firstName: 'missing' };
			const supervisor = userMap[assignedToSupervisorUserId] || {
				firstName: 'Not assigned yet.',
			};
			const hr = userMap[assignedToHrUserId] || { firstName: 'Not assigned yet.' };

			const row = {
				ptoRequestId,
				requestorDisplay: `${requestor.firstName} ${requestor.lastName}`,
				key: ptoRequestId,
				rangeDisplay,
				rangeSort,
				dateCreated,
				dateCreatedFormatted: moment(dateCreated).format('M/D/YY h:mm a'),
				typeDisplay: ptoTypeToDisplay(paidType),
				totalDaysSort: totalDays,
				totalDaysDisplay: totalDays ? `${totalDays}` : '-',
				totalHoursSort: totalHours,
				totalHoursDisplay: `${totalHours} hours`,
				isExported,
				status: (
					<>
						<div>{renderStatus(status)}</div>
						{!!linkedToRequiredRequestId && (
							<div>
								(
								<span role="img" aria-label="cal">
									📆
								</span>{' '}
								from schedule)
							</div>
						)}
					</>
				),
				assigned: (
					<>
						<div>
							Supervisor: {supervisor.firstName} {supervisor.lastName}
						</div>
						<div>
							HR: {hr.firstName} {hr.lastName}
						</div>
					</>
				),
				note: '',
				actionButtons: (
					<div style={{ display: 'flex' }}>
						<Button
							variant="outlined"
							className={classes.button}
							color="primary"
							onClick={() => {
								this.props.history.push(`/pto/exported/approve/${ptoRequestId}`);
							}}
						>
							View
						</Button>

						<Button
							variant="outlined"
							className={classes.button}
							color="primary"
							onClick={() => {
								this.updateExported({ row, action: 'hrRemovedExported' });
							}}
							style={{ whiteSpace: 'nowrap', marginLeft: 5 }}
						>
							Re-open
						</Button>
					</div>
				),
			};

			if (row.isExported) {
				hrExportedRequests.push(row);
			}
		});
		newState.hrExportedRequests = hrExportedRequests;
		this.setState(newState);
	};

	render() {
		const { classes } = this.props;
		const { hrExportedRequests, isReopening } = this.state;

		if (!hrExportedRequests || isReopening) {
			return (
				<div>
					<span style={{ fontSize: 24, padding: 20, fontWeight: 300, color: '#666' }}>
						Refreshing PTO Requests...
					</span>
				</div>
			);
		}

		const columns = [
			{ label: 'Employee', sortId: 'requestorDisplay', displayId: 'requestorDisplay' },
			{ label: 'Type', sortId: 'typeDisplay', displayId: 'typeDisplay' },
			{ label: 'First / Last Date', sortId: 'rangeSort', displayId: 'rangeDisplay' },
			{ label: 'Days', sortId: 'totalDaysSort', displayId: 'totalDaysDisplay' },
			{
				label: 'Total Hours',
				sortId: 'totalHoursSort',
				displayId: 'totalHoursDisplay',
			},
			{ label: 'Status', sortId: 'status', displayId: 'status' },
			{ label: 'Assigned', sortId: 'assigned', isNotSortable: true, displayId: 'assigned' },
			{
				label: 'Requested On',
				sortId: 'dateCreated',
				isNotSortable: true,
				displayId: 'dateCreatedFormatted',
			},
			{ label: 'Actions', sortId: 'fix', isNotSortable: true, displayId: 'actionButtons' },
		];

		return (
			<Fragment>
				<FilterHeader label={'HR Exported PTO Requests'} canMakeUpdates={true} />

				<Paper className={classes.globalMainPaper}>
					<EnhancedTable
						rows={hrExportedRequests}
						columns={columns}
						isDense={true}
						defaultOrder={'desc'}
						defaultOrderBy={'dateCreated'}
					/>
				</Paper>

				<Snackbar
					anchorOrigin={{
						vertical: 'bottom',
						horizontal: 'right',
					}}
					open={this.state.snackbarIsOpen}
					autoHideDuration={2000}
					onClose={this.handleSnackbarClose}
					TransitionComponent={SlideUpTransition}
				>
					<SnackbarContent
						className={classes.snackbarSuccess}
						aria-describedby="client-snackbar"
						message={
							<span id="client-snackbar">
								<CheckCircleIcon className={classes.snackbarIcon} />
								PTO request re-opened successfully.
							</span>
						}
						action={[
							<IconButton
								key="close"
								aria-label="close"
								color="inherit"
								onClick={this.handleSnackbarClose}
							>
								<CloseIcon className={classes.icon} />
							</IconButton>,
						]}
					/>
				</Snackbar>
			</Fragment>
		);
	}
}

PTOExportedDashboard.propTypes = {
	authUser: PropTypes.object,
	showErrors: PropTypes.func.isRequired,
	classes: PropTypes.object.isRequired,
	searchPtoRequests: PropTypes.func.isRequired,
	myPtoApprovals: PropTypes.array,
};

export default _.flow([
	withStyles(styles, { withTheme: true }),
	AuthUserContainer,
	PtoRequestContainer,
])(PTOExportedDashboard);
